import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useAuth } from '../auth/authContext'
import { ApiClient, type Identity, type Member, type Role, type TripSummary } from './api'
import { legeBeispielAn } from './beispiel'
import { adoptiereAltbestand, ALTBESTAND, altbestandVorhanden, CURSOR_KEY, getMeta } from './db'
import { TripContext, type TripPhase, type TripValue } from './tripContext'
import {
  besitzerVon,
  darfSchreiben as rolleSchreibt,
  darfVerwalten as rolleVerwaltet,
  fehlerSatz,
  rolleOderNull,
  sortiereMitglieder,
  sortiereReisen,
  waehleReise,
} from './trips'
import type { ZugriffWeg } from './sync'

/** Die zuletzt geöffnete Reise. Damit kostet der Normalfall keinen Klick. */
const LETZTE_KEY = 'roadbook:letzte-reise'
/** Die Reiseliste, damit die App auch ohne Netz startet. */
const LISTE_KEY = 'roadbook:reisen'
/** Die Mitglieder je Reise, aus demselben Grund. */
const MITGLIEDER_KEY = 'roadbook:mitglieder:'
/** Die eigene Identität, wie der Server sie sieht. */
const ICH_KEY = 'roadbook:ich'

/** Der Platzhalter für einen Bestand, der noch keiner Reise zugeordnet ist. */
const ALTBESTAND_REISE: TripSummary = {
  id: ALTBESTAND,
  name: 'Nicht zugeordnete Reise',
  role: 'admin',
  createdBy: '',
  createdAt: 0,
  updatedAt: 0,
}

function lies<T>(key: string): T | null {
  try {
    const roh = localStorage.getItem(key)
    return roh ? (JSON.parse(roh) as T) : null
  } catch {
    // Kein localStorage oder kaputter Eintrag: dann eben ohne Zwischenspeicher.
    return null
  }
}

function schreibe(key: string, wert: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(wert))
  } catch {
    // Voll oder gesperrt. Der Zwischenspeicher ist eine Bequemlichkeit, kein
    // Bestandteil des Bestands.
  }
}

function loesche(...keys: string[]): void {
  try {
    for (const key of keys) localStorage.removeItem(key)
  } catch {
    // Siehe oben.
  }
}

/**
 * Alle gemerkten Mitgliederlisten, gleich zu welcher Reise.
 *
 * Über den Schlüsselraum und nicht über die bekannte Reiseliste: beim
 * Abmelden zählt, was auf dem Gerät liegt, und nicht, was die App gerade
 * geladen hat. Sonst bliebe die Liste einer Reise stehen, in der der Nutzer
 * heute nicht war - mit den Adressen fremder Leute darin.
 */
function mitgliederSchluessel(): string[] {
  const keys: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(MITGLIEDER_KEY)) keys.push(key)
    }
  } catch {
    // Kein localStorage: dann gibt es auch nichts zu vergessen.
  }
  return keys
}

/** Nur was Hand und Fuss hat, kommt aus dem Zwischenspeicher zurück. */
function brauchbareListe(wert: unknown): TripSummary[] | null {
  if (!Array.isArray(wert)) return null
  const liste: TripSummary[] = []
  for (const eintrag of wert as Partial<TripSummary>[]) {
    const rolle = rolleOderNull(eintrag.role)
    if (typeof eintrag.id !== 'string' || eintrag.id === '' || !rolle) return null
    liste.push({
      id: eintrag.id,
      name: typeof eintrag.name === 'string' ? eintrag.name : 'Reise',
      role: rolle,
      createdBy: typeof eintrag.createdBy === 'string' ? eintrag.createdBy : '',
      createdAt: typeof eintrag.createdAt === 'number' ? eintrag.createdAt : 0,
      updatedAt: typeof eintrag.updatedAt === 'number' ? eintrag.updatedAt : 0,
    })
  }
  return liste
}

/**
 * Hält die Reisen: welche es gibt, welche offen ist, wer darin mitfährt.
 *
 * Diese Schicht liegt zwischen der Anmeldung und dem Bestand. Sie muss vor dem
 * Bestand stehen, weil ohne Reise nicht einmal feststeht, welche Datenbank
 * gemeint ist - und sie muss hinter der Anmeldung stehen, weil die Reisen am
 * Token hängen.
 *
 * Offline ist sie ausdrücklich arbeitsfähig: die Liste und die Mitglieder
 * liegen im localStorage, und wer gestern in einer Reise war, kommt heute im
 * Funkloch wieder hinein. Ohne diesen Zwischenspeicher wäre die App ohne Netz
 * gar nicht mehr zu öffnen - und genau dafür ist sie gebaut.
 */
export function TripProvider({ children }: { children: ReactNode }) {
  const { getToken, status: authStatus, email: authEmail } = useAuth()

  const [phase, setPhase] = useState<TripPhase>('laedt')
  const [trips, setTrips] = useState<TripSummary[]>(
    () => brauchbareListe(lies<unknown>(LISTE_KEY)) ?? [],
  )
  const [aktivId, setAktivId] = useState<string | null>(null)
  const [istAltbestand, setIstAltbestand] = useState(false)
  const [mitglieder, setMitglieder] = useState<Member[] | null>(null)
  const [ich, setIch] = useState<Identity | null>(() => lies<Identity>(ICH_KEY))
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const api = useMemo(() => new ApiClient(getToken), [getToken])

  /** Wahr, sobald vergissAlles gelaufen ist: danach wird nichts mehr geholt. */
  const stillgelegt = useRef(false)

  const merkeListe = useCallback((liste: TripSummary[]) => {
    setTrips(liste)
    schreibe(LISTE_KEY, liste)
  }, [])

  const oeffne = useCallback((tripId: string) => {
    setAktivId(tripId)
    setMeldung(null)
    setPhase('offen')
    if (tripId !== ALTBESTAND) schreibe(LETZTE_KEY, tripId)
  }, [])

  const zurListe = useCallback((text?: string) => {
    setAktivId(null)
    setMitglieder(null)
    setMeldung(text ?? null)
    setPhase('auswahl')
  }, [])

  /**
   * Holt die Reiseliste vom Server.
   *
   * Scheitert der Abruf, bleibt die zuletzt gespeicherte Liste stehen. Das ist
   * kein Notbehelf, sondern der Regelfall unterwegs: ohne Netz gibt es keine
   * frische Liste, und die App muss trotzdem aufgehen.
   *
   * @returns die Liste, mit der weitergearbeitet wird.
   */
  const holeListe = useCallback(async (): Promise<TripSummary[]> => {
    try {
      const geholt = sortiereReisen(await api.listTrips())
      if (stillgelegt.current) return geholt
      merkeListe(geholt)
      setFehler(null)
      return geholt
    } catch (err) {
      console.warn('Reisen liessen sich nicht laden', err)
      const gespeichert = brauchbareListe(lies<unknown>(LISTE_KEY))
      if (!stillgelegt.current) setFehler(fehlerSatz(err))
      return gespeichert ?? []
    }
  }, [api, merkeListe])

  /**
   * Die gerade laufende Zuordnung des Altbestands, oder nichts.
   *
   * Dieser Effekt hängt am Anmeldezustand und läuft deshalb bei jedem Wechsel
   * zwischen offline und ready erneut an - so ist es gewollt, denn beim ersten
   * Anlauf ohne Netz kommt die Zuordnung nicht zustande. Zwei Zuordnungen
   * gleichzeitig sollen daraus trotzdem nicht werden, und schon gar nicht eine
   * neben dem Anlegen einer Reise: beide entschieden dann für sich, wem der
   * Bestand gehört. Der eigentliche Riegel dagegen liegt eine Schicht tiefer
   * (adoptiereAltbestand prüft und schreibt in einer Transaktion); hier geht es
   * darum, den Fall gar nicht erst entstehen zu lassen.
   */
  const zuordnung = useRef<Promise<string | null> | null>(null)

  /** Wartet eine laufende Zuordnung ab. Ohne eine solche: sofort null. */
  const zuordnungAbwarten = useCallback(
    (): Promise<string | null> => zuordnung.current ?? Promise.resolve(null),
    [],
  )

  /**
   * Ordnet einen Bestand ohne Reise-ID der Reise zu, aus der er stammt.
   *
   * Gefragt wird der Server, nicht geraten: auf dem Altweg löst er die älteste
   * selbst angelegte Reise auf (store.LegacyTripFor). Das ist die Klasse von
   * Reisen, aus der ein Bestand überhaupt stammen kann, und für einen Bestand,
   * der schon einmal abgeglichen hat, auch genau die richtige. Für einen
   * Bestand, der nie abgeglichen hat, ist es eine begründete Annahme und kein
   * Beweis - dessen Abgleichzeiger steht dann aber auf 0, der erste Pull holt
   * also alles, und übersprungen wird nichts. Geraten hat der Client dabei
   * nichts: eine geratene Zuordnung wäre die eine Vermischung zweier Reisen,
   * die sich hinterher nicht mehr trennen lässt.
   *
   * @returns die Reise-ID, oder null wenn die Zuordnung offen bleibt.
   */
  const ordneAltbestandZu = useCallback((): Promise<string | null> => {
    const laufend = zuordnung.current
    if (laufend) return laufend

    const neue = (async () => {
      try {
        const since = (await getMeta<number>(ALTBESTAND, CURSOR_KEY)) ?? 0
        const tripId = await api.legacyTripId(since)
        if (!tripId) return null
        await adoptiereAltbestand(tripId)
        return tripId
      } catch (err) {
        console.warn('Altbestand liess sich noch nicht zuordnen', err)
        return null
      }
    })()
    zuordnung.current = neue
    void neue.finally(() => {
      if (zuordnung.current === neue) zuordnung.current = null
    })
    return neue
  }, [api])

  /**
   * Wahr, sobald einmal entschieden wurde, welche Reise aufgeht.
   *
   * Der Anmeldezustand wechselt unterwegs ständig zwischen ready und offline,
   * und dieser Effekt hängt daran: ohne die Marke risse er den Nutzer bei
   * jedem Wechsel aus der Auswahl heraus und schlüge wieder die zuletzt
   * benutzte Reise auf. Nachgeholt wird trotzdem, was beim ersten Anlauf ohne
   * Netz nicht ging - die Liste und die Zuordnung des Altbestands.
   */
  const entschieden = useRef(false)

  // Erststart: wer bin ich, welche Reisen gibt es, und liegt hier noch ein
  // Bestand ohne Reise?
  useEffect(() => {
    if (authStatus !== 'ready' && authStatus !== 'offline') return
    let abgebrochen = false

    void (async () => {
      // Die Identität kommt vom Server und nicht aus dem Token: im
      // Entwicklungsmodus ohne Anmeldung gibt es gar keines, und die
      // Mitgliedschaft hängt an der Adresse, die der Server sieht.
      api.fetchIdentity().then(
        (identitaet) => {
          if (abgebrochen || stillgelegt.current) return
          setIch(identitaet)
          schreibe(ICH_KEY, identitaet)
        },
        (err: unknown) => {
          console.warn('Identität liess sich nicht laden', err)
        },
      )

      let altbestand = await altbestandVorhanden().catch(() => false)
      const liste = await holeListe()
      if (abgebrochen) return

      if (altbestand) {
        const zugeordnet = await ordneAltbestandZu()
        if (abgebrochen) return
        if (zugeordnet) {
          altbestand = false
          // Nach der Zuordnung ist die Reise vielleicht neu in der Liste - sie
          // stammt aus der Migration und war vorher nie zu sehen.
          if (!liste.some((t) => t.id === zugeordnet)) {
            const frisch = await holeListe()
            if (abgebrochen) return
            liste.splice(0, liste.length, ...frisch)
          }
          setIstAltbestand(false)
          // Der Bestand liegt jetzt in dieser Reise: dorthin gehört der
          // Nutzer, auch wenn er gerade woanders steht.
          entschieden.current = true
          oeffne(zugeordnet)
          return
        }
      }

      setIstAltbestand(altbestand)

      // Ab hier geht es nur noch darum, welche Reise aufgeht - und das ist
      // längst entschieden, wenn dieser Durchgang bloss dem
      // Wiedererreichbarwerden des Servers folgt.
      if (entschieden.current) return
      entschieden.current = true

      const gewaehlt = waehleReise(liste, lies<string>(LETZTE_KEY))
      if (gewaehlt) {
        oeffne(gewaehlt.id)
        return
      }
      if (liste.length === 0 && altbestand) {
        // Kein Netz und ein Bestand, der noch keiner Reise gehört: die App
        // läuft trotzdem, nur eben ohne Abgleich. Das Tagebuch zuzusperren,
        // weil der Server gerade nicht antwortet, wäre das Gegenteil von
        // offline-first.
        oeffne(ALTBESTAND)
        return
      }
      setPhase('auswahl')
    })()

    return () => {
      abgebrochen = true
    }
  }, [authStatus, api, holeListe, ordneAltbestandZu, oeffne])

  const aktiv = useMemo<TripSummary | null>(() => {
    if (aktivId === null) return null
    if (aktivId === ALTBESTAND) return ALTBESTAND_REISE
    return trips.find((t) => t.id === aktivId) ?? null
  }, [aktivId, trips])

  // Eine offene Reise, die aus der Liste verschwunden ist: die Mitgliedschaft
  // ist weg oder die Reise gelöscht. Zurück zur Auswahl - und ausdrücklich
  // ohne den lokalen Bestand anzufassen.
  useEffect(() => {
    if (phase !== 'offen' || aktivId === null || aktivId === ALTBESTAND) return
    if (trips.length === 0) return
    if (trips.some((t) => t.id === aktivId)) return
    zurListe(
      'Diese Reise steht nicht mehr in deiner Liste — sie wurde gelöscht, oder die Mitgliedschaft wurde entzogen. Der Bestand auf diesem Gerät bleibt erhalten.',
    )
  }, [phase, aktivId, trips, zurListe])

  const rolle: Role = aktiv?.role ?? 'read'
  const darfSchreiben = rolleSchreibt(rolle)
  const darfVerwalten = rolleVerwaltet(rolle)

  const mitgliederLaden = useCallback(async () => {
    if (!aktivId || aktivId === ALTBESTAND) return
    // Die Mitgliederliste steht erst ab der Rolle Bearbeiten offen (der Server
    // verlangt modify). Sie mit der Rolle Nur lesen anzufragen brachte bei
    // jedem Start ein 403 in die Konsole und nie eine Antwort.
    if (!rolleSchreibt(rolle)) {
      setMitglieder(null)
      return
    }
    try {
      const geholt = sortiereMitglieder(await api.listMembers(aktivId))
      if (stillgelegt.current) return
      setMitglieder(geholt)
      schreibe(MITGLIEDER_KEY + aktivId, geholt)
    } catch (err) {
      console.warn('Mitglieder liessen sich nicht laden', err)
      const gespeichert = lies<Member[]>(MITGLIEDER_KEY + aktivId)
      if (!stillgelegt.current) setMitglieder(gespeichert)
    }
  }, [api, aktivId, rolle])

  useEffect(() => {
    if (phase !== 'offen') return
    // Der gespeicherte Stand steht sofort da, der frische kommt gleich. Ohne
    // das erste bliebe die Zuschreibung des alten Tagebuchtexts offline
    // dauerhaft unentschieden.
    if (aktivId && aktivId !== ALTBESTAND && rolleSchreibt(rolle)) {
      setMitglieder(lies<Member[]>(MITGLIEDER_KEY + aktivId))
    }
    void mitgliederLaden()
  }, [phase, aktivId, rolle, mitgliederLaden])

  const besitzer = useMemo(() => besitzerVon(mitglieder), [mitglieder])
  const meineEmail = ich?.email ?? authEmail

  const neuLaden = useCallback(async () => {
    await holeListe()
  }, [holeListe])

  const anlegen = useCallback(
    async (name: string, mitBeispiel = false) => {
      const neu = await api.createTrip(name)
      // Eine Zuordnung, die im Hintergrund läuft, erst zu Ende bringen. Sonst
      // entscheiden zwei Wege gleichzeitig, wem der Bestand gehört, und der
      // Verlierer trägt danach für immer eine Reise-ID, unter der sich seine
      // Datenbank nicht mehr öffnen lässt.
      const schonZugeordnet = await zuordnungAbwarten()
      // Der Bestand auf diesem Gerät gehört dem Nutzer, und es gibt keine
      // andere Reise, zu der er passen könnte: er wandert in die frisch
      // angelegte. Der Abgleichzeiger fällt dabei auf 0 zurück - er zeigt in
      // den Änderungsstrom einer anderen Reise, und in diesem hier stünde er
      // zu weit vorn.
      if (istAltbestand && !schonZugeordnet && trips.length === 0) {
        await adoptiereAltbestand(neu.id, true).catch((err: unknown) => {
          console.error('Altbestand liess sich nicht zuordnen', err)
        })
        setIstAltbestand(false)
      }
      // Die Vorlage vor dem Öffnen, nicht danach: sobald oeffne() gelaufen
      // ist, baut App.tsx den RoadbookProvider auf und der liest den Bestand.
      // Käme sie später, sähe der Nutzer erst eine leere Reise und dann
      // sechzehn Etappen, die aus dem Nichts erscheinen.
      //
      // Nach der Zuordnung des Altbestands und nicht davor: legeBeispielAn
      // schreibt nur, was fehlt, und ein zugeordneter Altbestand bringt sein
      // Reisedokument mit. Die Vorlage darf es nicht überschreiben.
      if (mitBeispiel) {
        await legeBeispielAn(neu.id).catch((err: unknown) => {
          // Kein Grund, die Reise nicht zu öffnen: sie ist angelegt, sie ist
          // leer, und leer ist ein brauchbarer Zustand.
          console.error('Beispieldaten liessen sich nicht anlegen', err)
        })
      }
      merkeListe(sortiereReisen([...trips, neu]))
      oeffne(neu.id)
      void holeListe()
    },
    [api, trips, istAltbestand, merkeListe, oeffne, holeListe, zuordnungAbwarten],
  )

  const umbenennen = useCallback(
    async (name: string) => {
      if (!aktivId || aktivId === ALTBESTAND) return
      const umbenannt = await api.renameTrip(aktivId, name)
      merkeListe(sortiereReisen(trips.map((t) => (t.id === umbenannt.id ? umbenannt : t))))
    },
    [api, aktivId, trips, merkeListe],
  )

  const loeschen = useCallback(async () => {
    if (!aktivId || aktivId === ALTBESTAND) return
    await api.deleteTrip(aktivId)
    merkeListe(trips.filter((t) => t.id !== aktivId))
    loesche(MITGLIEDER_KEY + aktivId, LETZTE_KEY)
    zurListe('Die Reise ist gelöscht.')
  }, [api, aktivId, trips, merkeListe, zurListe])

  const setzeMitglied = useCallback(
    async (email: string, neueRolle: Role) => {
      if (!aktivId || aktivId === ALTBESTAND) return
      await api.putMember(aktivId, email, neueRolle)
      await mitgliederLaden()
      // Die eigene Rolle kann sich dabei nicht geändert haben - der Server
      // lässt niemanden an die eigene Mitgliedschaft -, wohl aber die Liste
      // der Reisen eines anderen. Hier genügt der frische Mitgliederstand.
    },
    [api, aktivId, mitgliederLaden],
  )

  const entferneMitglied = useCallback(
    async (email: string) => {
      if (!aktivId || aktivId === ALTBESTAND) return
      await api.removeMember(aktivId, email)
      await mitgliederLaden()
    },
    [api, aktivId, mitgliederLaden],
  )

  const zugriffVerloren = useCallback(
    (grund: ZugriffWeg) => {
      zurListe(
        grund === 'verboten'
          ? 'Deine Rolle in dieser Reise reicht nicht mehr für den Abgleich. Der Bestand auf diesem Gerät bleibt erhalten.'
          : 'Diese Reise gibt es für dich nicht mehr — sie wurde gelöscht, oder die Mitgliedschaft wurde entzogen. Der Bestand auf diesem Gerät bleibt erhalten.',
      )
      void holeListe()
    },
    [zurListe, holeListe],
  )

  /**
   * Beim Abmelden: alles vergessen, was über die Reisen dieses Nutzers
   * Auskunft gibt.
   *
   * Der Bestand selbst wird woanders gelöscht (wipeForSignOut). Hier geht es
   * um die Liste, die Mitglieder und die Identität - auf einem geteilten
   * Gerät sagte sonst der Zwischenspeicher dem Nächsten, mit wem der
   * Vorgänger unterwegs war.
   */
  const vergissAlles = useCallback(() => {
    stillgelegt.current = true
    loesche(LISTE_KEY, LETZTE_KEY, ICH_KEY, ...mitgliederSchluessel())
    setTrips([])
    setMitglieder(null)
    setIch(null)
  }, [])

  const value = useMemo<TripValue>(
    () => ({
      phase,
      trips,
      aktiv,
      istAltbestand: aktivId === ALTBESTAND,
      rolle,
      darfSchreiben,
      darfVerwalten,
      mitglieder,
      besitzer,
      meineEmail,
      meldung,
      fehler,
      oeffne,
      zurListe,
      neuLaden,
      anlegen,
      umbenennen,
      loeschen,
      mitgliederLaden,
      setzeMitglied,
      entferneMitglied,
      zugriffVerloren,
      vergissAlles,
      api,
    }),
    [
      phase,
      trips,
      aktiv,
      aktivId,
      rolle,
      darfSchreiben,
      darfVerwalten,
      mitglieder,
      besitzer,
      meineEmail,
      meldung,
      fehler,
      oeffne,
      zurListe,
      neuLaden,
      anlegen,
      umbenennen,
      loeschen,
      mitgliederLaden,
      setzeMitglied,
      entferneMitglied,
      zugriffVerloren,
      vergissAlles,
      api,
    ],
  )

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>
}
